import { useId } from 'react';

import { useControllableState } from '@mint-lib/hooks';
import {
  SelectContent,
  SelectItem,
  Select as SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@mint-lib/tw-components';
import {
  ComboboxInputHelperText,
  ComboboxInputLabel,
  ComboboxInputRoot,
} from '@mint-lib/tw-components/patterns';

import { Loader } from './Loader.jsx';

type SelectOption = {
  label: string;
  value: string;
};

type SelectProps<T = SelectOption> = {
  label?: string;
  placeholder?: string;
  infoIconWithTooltip?: string;
  inputValue?: string | null;
  defaultInputValue?: string | null;
  options: T[];
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  onChange: (value: T) => void;
  isLoading?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'small' | 'medium';
  color?: 'default' | 'error' | 'warning';
  className?: string;
  helperText?: string;
  helperTextColor?: 'default' | 'error' | 'warning';
};

export const Select = <T,>({
  className,
  label,
  infoIconWithTooltip,
  placeholder,
  inputValue: controlledInputValue,
  defaultInputValue,
  options,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionLabel = (v: any) => v,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionValue = (v: any) => v,
  onChange,
  isLoading,
  disabled,
  readOnly,
  size = 'medium',
  color,
  helperText,
  helperTextColor,
}: SelectProps<T>) => {
  const id = useId();

  const [inputValue, setInputValue] = useControllableState({
    prop: controlledInputValue,
    defaultProp: defaultInputValue,
  });

  const handleChange = (optionLabel: string) => {
    const option = options.find((opt) => getOptionLabel(opt) === optionLabel);
    if (option) {
      onChange(option);
      setInputValue(optionLabel);
    }
  };

  return (
    <ComboboxInputRoot className={className}>
      {label && (
        <ComboboxInputLabel
          label={label}
          htmlFor={id}
          infoIconWithTooltip={infoIconWithTooltip}
        />
      )}
      <SelectRoot value={inputValue ?? undefined} onValueChange={handleChange}>
        <SelectTrigger
          id={id}
          size={size}
          color={color}
          disabled={disabled}
          readOnly={readOnly}
        >
          <SelectValue placeholder={placeholder}>
            <span>{inputValue}</span>
          </SelectValue>
        </SelectTrigger>
        {!readOnly && (
          <SelectContent>
            {isLoading ? (
              <div className="p-4">
                <Loader />
              </div>
            ) : (
              options.map((option) => (
                <SelectItem
                  key={getOptionValue(option)}
                  value={getOptionLabel(option)}
                >
                  {getOptionLabel(option)}
                </SelectItem>
              ))
            )}
          </SelectContent>
        )}
      </SelectRoot>
      {helperText && (
        <ComboboxInputHelperText
          helperText={helperText}
          color={helperTextColor}
        />
      )}
    </ComboboxInputRoot>
  );
};
